import {ChangeDetectorRef, Component, forwardRef, Input, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {FormArray, FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {Subject} from "rxjs/Subject";
import {DispatchListItem} from "../../../shared/view-model/store-management/dispatch-list-item.type";
import {DepotService, FetchDepotsResult} from "../../services/depot-service/depot.service";
import {ApolloQueryResult} from "apollo-client";
import {BehaviorSubject} from "rxjs/BehaviorSubject";
import {SubAdminUnitNeedsItemsComponent} from "../sub-admin-unit-needs-items/sub-admin-unit-needs-items.component";
import {NgRedux} from "@angular-redux/store";
import {IAppState} from "../../../shared/redux-store/store";
import {Depot} from "../../../shared/view-model/store-management/depot.type";
import {Location} from "@angular/common";
import {isUndefined} from "util";
import {ActivatedRoute} from "@angular/router";
import {filter} from "rxjs/operators";
import {appInjector} from "../../../index";
import {DispatchList} from "../../../shared/view-model/store-management/dispatch-list.type";
import {ProcurementService} from "../../services/procurement-service/procurement.service";
import {ApolloQueryObservable} from "apollo-angular";
import {AbstractVisbosControl} from "../../../core/visbos-core/ui/forms/abstract-visbos-control";
import {SessionActions} from "../../../shared/actions/session.actions";
import {SubAdminUnitNeedsComponent} from "../sub-admin-unit-needs/sub-admin-unit-needs.component";
import {FillUpDepotMutation} from "../../../shared/graphql/graphql.types";
import {FileService} from "../../../core/visbos-core/http/file.service";
import {MedicineAuthFormComponent} from "../../withdrawal/medicine-auth-form/medicine-auth-form.component";
import {AdministrationUnit} from "../../../shared/view-model/organization/administration-unit.type";

@Component({
  selector: 'vb-depot-needs',
  templateUrl: './depot-needs.component.html',
  styleUrls: ['./depot-needs.component.css'],
  providers: [{provide: AbstractVisbosControl, useExisting: forwardRef(() => DepotNeedsComponent)}]
})
export class DepotNeedsComponent extends AbstractVisbosControl<DispatchList, FormGroup> implements OnInit {

  @ViewChild(SubAdminUnitNeedsItemsComponent)
  dispatchItemsCmp: SubAdminUnitNeedsItemsComponent;

  @Input()
  activeDepotCuid: string;

  @Input()
  showDepotSelection: boolean = true;

  @Input()
  showPrintVouchersCheckBox: boolean = true;

  @Input()
  showSrcSelection: boolean = true;

  private dispatchList$: ApolloQueryObservable<any>;
  dispatchList: DispatchList;

  private activeDepotCuid$: Subject<string> = new BehaviorSubject(null);
  private activeDepot$: ApolloQueryObservable<FetchDepotsResult>;
  activeDepot: Depot;

  selectedItems = [];

  medicineAuthForm: FormGroup;
  generateVouchersCtrl: FormControl;

  sourceAdminUnits: AdministrationUnit[] = [];
  srcAdminUnitCuid$: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  constructor(fb: FormBuilder, cd: ChangeDetectorRef, sessionActions: SessionActions,
              private fileService: FileService,
              private route: ActivatedRoute,
              private loc: Location,
              private depotService: DepotService,
              private procurementService: ProcurementService,
              private ngRx: NgRedux<IAppState>) {
    super(fb, cd, sessionActions);

  }

  ngOnInit() {
    super.ngOnInit();

    this.generateVouchersCtrl = this.fb.control(false);

    this.activeDepot$ = this.depotService.getDepot(this.activeDepotCuid$);
    this.addComponentData(this.activeDepot$);
    this.addSubscription(this.activeDepot$.subscribe(res => {
      this.activeDepot = res.data.fetchDepots[0];
      this.sourceAdminUnits = this.activeDepot.managed_by_admin_units;
    }));

    this.dispatchList$ = this.procurementService.getDispatchListForDepot(
      this.activeDepotCuid$,
      this.srcAdminUnitCuid$.pipe(filter(i => i !== null))
    );
    this.addComponentData(this.dispatchList$);
    this.dataSubscriptions.push(
      this.dispatchList$.subscribe(res => {
        this.dispatchList = res.data.dispatchListForDepot;
        this.setDefaultValues(this.dispatchList);
      })
    );

    this.addSubscription(this.route.params.subscribe(params => {
        if (params.depotCuid) {
          this.activeDepotCuid$.next(params.depotCuid);
          this.showDepotSelection = false;
        }
      })
    );

  }


  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);

    if (changes.activeDepotCuid) {
      this.activeDepotCuid$.next(this.activeDepotCuid);
      if (this.activeDepotCuid) {
        this.showDepotSelection = false;
      }
    }
  }

  onDepotSelected(depot: Depot) {
    this.activeDepotCuid$.next(depot.cuid);
  }

  get depotLabel(): string {

    if (!this.activeDepot || !this.activeAdminUnit) {
      return "<i class='far fa-spinner fa-spin'></i>";
    }

    if (this.activeDepot.administration_unit.cuid == this.activeAdminUnit.cuid) {
      return '"' + this.activeDepot.label + '"';
    } else {
      return '"' + this.activeDepot.label + ' (' + this.activeDepot.administration_unit.label + ')"'
    }

  }

  buildControlContent(values?: DispatchList): FormGroup {
    return SubAdminUnitNeedsComponent.initControl(this.fb);
  }

  public static initControl(fb: FormBuilder = null): FormGroup {
    fb = fb ? fb : appInjector().get(FormBuilder);

    return fb.group({
      cuid: [null],
      fill_up_date: [null, Validators.required],
      user_id: [null],
      supplier_administration_unit_id: [null, Validators.required],
      recipient_administration_unit_id: [null, Validators.required],
      fill_up_items: fb.array([], Validators.required)
    });
  }

  public setDefaultValues(item?: DispatchList): { [controlKey: string]: any } | any {

    if (!item) {
      return undefined;
    }

    let val = {
      cuid: AbstractVisbosControl.cuid(),
      fill_up_date: new Date().toISOString().slice(0, 10),
      supplier_administration_unit_id: this.ngRx.getState().session.activeAdminUnit,
      recipient_administration_unit_id: this.activeDepot.administration_unit.cuid,
      user_id: this.ngRx.getState().session.user.cuid
    };

    if (!isUndefined(val)) {
      this.control.patchValue(val);
    }

    this.addDispatchItems(item, <FormArray>this.control.get('fill_up_items'));

    return val;

  }

  addDispatchItems(dispatchList: DispatchList, formArray: FormArray) {
    formArray.controls = [];
    let orderItems: DispatchListItem[] = dispatchList.dispatch_list_items;

    orderItems.forEach(item => {
      formArray.push(this.initDispatchItemControl(dispatchList, item))
    })
  }

  initDispatchItemControl(dispatchList?: DispatchList, values?: DispatchListItem): FormGroup {

    return this.fb.group({
      cuid: [this.setValue(values, 'cuid')],
      stock_item_id: [this.setValue(values, 'stock_item.cuid')],
      amount: [this.setValue(values, 'amount'), Validators.required],
      product_packing_id: [this.setValue(values, 'product_packing.cuid')],
      packing_unit: [this.setValue(values, 'product_packing.packing_unit.packing_unit_name')],
      packing_content: [this.setValue(values, 'product_packing.packing_amount')],
      price: [this.setValue(values, 'product_packing.packing_price')],
      source_stock_id: [this.setValue(values, 'source_stocks.0.cuid')],
      target_stock_id: [this.setValue(values, 'target_stocks.0.cuid'), Validators.required],
      product_batches: this.fb.array([])
    });

  }

  onGenerateDispatchListPdfClick() {
    return this.procurementService.generateDispatchListPDF(this.dispatchList).toPromise();
  }

  onDispatchItemsClick(): Promise<ApolloQueryResult<FillUpDepotMutation>> {

    let input = Object.assign({}, this.control.value);
    input.fill_up_items = input.fill_up_items.filter(item => this.selectedItems.find(sel => sel === item.cuid));

    const authData = this.medicineAuthForm ? this.medicineAuthForm.value : null;
    const generateVouchers: boolean = this.generateVouchersCtrl ? this.generateVouchersCtrl.value : false;

    return this.procurementService.fillUpDepot(input, authData, generateVouchers).toPromise();

  }

  onSelectedChanged(selected: any[]) {
    this.selectedItems = selected;

    let authItem: boolean = this.selectedItems && this.selectedItems.length > 0 && this.dispatchList.dispatch_list_items.findIndex(item => {
      return (this.selectedItems.findIndex(id => id === item.cuid) > -1) &&
        (item.source_stocks.findIndex(s => s.registration_required === true) > -1 ||
          item.target_stocks.findIndex(s => s.registration_required === true) > -1);
    }) > -1;

    if (!this.medicineAuthForm && authItem) {
      this.createMedicineAuthComponent();
    } else if (this.medicineAuthForm && !authItem) {
      this.removeMedicineAuthForm();
    }

  }

  canSend(): boolean {
    let items = this.selectedItems && this.selectedItems.length > 0;
    let auth = this.medicineAuthForm ? this.medicineAuthForm.valid : true;
    return items && auth;
  }

  canGenerateDispatchListPdf() {
    return this.dispatchList != null;
  }

  createMedicineAuthComponent() {
    this.medicineAuthForm = MedicineAuthFormComponent.initControl(this.fb);
    this.cd.detectChanges();
  }

  removeMedicineAuthForm() {
    this.medicineAuthForm = null;
    this.cd.detectChanges();
  }

  srcAdminUnitSelected(adminUnit) {
    this.srcAdminUnitCuid$.next(adminUnit ? adminUnit.cuid : null);
  }

}


// WEBPACK FOOTER //
// ./src/app/store-management/procurement/depot-needs/depot-needs.component.ts